import {Dispatch, SetStateAction, useRef, useState} from "react";
import {DateRange} from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import styled from "styled-components";
import theme from "../../style/theme";
import {dateFormat} from "../../utils/date/date-format";
import CalendarLineIcon from "../icons/CalendarLineIcon/CalendarLineIcon";
import {useClickOutside} from "../../utils/click/handle-click";

export interface DateRangeState {
    startDate?: Date;
    endDate?: Date;
    key?: string;
}

interface Props {
    dateRange: DateRangeState[];
    setDateRange: Dispatch<SetStateAction<DateRangeState[]>>;
}

const DateRangePicker: React.FC<Props> = (
    {
        dateRange,
        setDateRange
    }
) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const handleClickBox = () => {
        setOpen(!open);
    };

    useClickOutside(ref, () => setOpen(false));

    return (
        <DataRangePickerContainer ref={ref}>
            <DateRangePickerOutlinedButton onClick={handleClickBox}>
                <DateRangePickerOutlinedButtonLabel>{`${dateFormat(
                    dateRange[0].startDate
                )} ~ ${dateFormat(
                    dateRange[0].endDate
                )}`}</DateRangePickerOutlinedButtonLabel>
                <CalendarLineIcon
                    width="1.125rem"
                    height="1.125rem"
                    fill={theme.palette.primitives.gray500}
                />
            </DateRangePickerOutlinedButton>
            {open && (
                <DateRangeContainer>
                    <DateRange
                        editableDateInputs={true}
                        onChange={(item) => setDateRange([item.selection])}
                        moveRangeOnFirstSelection={false}
                        ranges={dateRange}
                        rangeColors={[theme.palette.primitives.yellow400]}
                    />
                </DateRangeContainer>
            )}
        </DataRangePickerContainer>
    );
};

export default DateRangePicker;

const DataRangePickerContainer = styled.div`
  z-index: 1;
`;

const DateRangePickerOutlinedButton = styled.div`
  display: flex;
  width: 14.3rem;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  cursor: pointer;
  background: ${theme.palette.primitives.yellow50};
  border: 1px solid ${theme.palette.primitives.yellow400};
  border-radius: 4px;
`;

const DateRangePickerOutlinedButtonLabel = styled.div`
  font-weight: 500;
  font-size: 0.938rem;
  line-height: 1.125rem;
  color: ${theme.palette.primitives.gray700};
  margin-right: 0.75rem;
`;

const DateRangeContainer = styled.div`
  position: absolute;

  .rdrDayToday .rdrDayNumber span:after {
    background-color: ${theme.palette.primitives.yellow400};
  }

  .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span,
  .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span,
  .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span,
  .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span {
    color: black;
  }

  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.2);
`;
